
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, AlertCircle, Save, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";

const SettingsAdmin = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { 
    data: settings = [], 
    isLoading, 
    isError 
  } = useQuery({
    queryKey: ['settings'],
    queryFn: api.settings.getAll
  });
  
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  
  // Initialize form values when settings load
  useState(() => {
    if (settings.length > 0 && Object.keys(formValues).length === 0) {
      const initialValues: Record<string, string> = {};
      settings.forEach((setting: any) => {
        initialValues[setting.key] = setting.value || '';
      });
      setFormValues(initialValues);
    }
  });
  
  const handleInputChange = (key: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // Save each changed setting
      const updatePromises = Object.entries(formValues).map(([key, value]) => {
        const originalSetting = settings.find((s: any) => s.key === key);
        if (originalSetting && originalSetting.value !== value) {
          return api.settings.update(key, value);
        }
        return Promise.resolve();
      });
      
      await Promise.all(updatePromises);
      
      toast({
        title: "Settings saved",
        description: "Your settings have been updated successfully"
      });
      
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error saving settings",
        description: "An error occurred while saving your settings",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (isError) {
    return (
      <div className="flex justify-center items-center py-8 text-destructive">
        <AlertCircle className="h-8 w-8 mr-2" />
        <p>Error loading settings. Please try again.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Settings</h1>
        <Button onClick={handleSaveSettings} disabled={isSaving}>
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Changes
        </Button>
      </div>
      <div className="flex space-x-4">
        <Link to="/admin/dictionaries">
          <Button variant="outline" className="flex items-center">
            <BookOpen className="mr-2 h-4 w-4" />
            Справочники
          </Button>
        </Link>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Site Settings</CardTitle>
          <CardDescription>
            General settings for your website
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {settings
            .filter((setting: any) => setting.key !== 'contact_email')
            .map((setting: any) => (
              <div key={setting.key} className="space-y-2">
                <Label htmlFor={setting.key}>
                  {setting.key.split('_').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </Label>
                {setting.key.includes('description') ? (
                  <Textarea 
                    id={setting.key}
                    value={formValues[setting.key] || ''}
                    onChange={(e) => handleInputChange(setting.key, e.target.value)}
                    placeholder={setting.description}
                    rows={3}
                  />
                ) : (
                  <Input 
                    id={setting.key}
                    value={formValues[setting.key] || ''}
                    onChange={(e) => handleInputChange(setting.key, e.target.value)}
                    placeholder={setting.description}
                  />
                )}
                {setting.description && (
                  <p className="text-sm text-muted-foreground">{setting.description}</p>
                )}
              </div>
            ))}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription>
            Contact details for your website
          </CardDescription>
        </CardHeader>
        <CardContent>
          {settings
            .filter((setting: any) => setting.key === 'contact_email')
            .map((setting: any) => (
              <div key={setting.key} className="space-y-2">
                <Label htmlFor={setting.key}>
                  {setting.key.split('_').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </Label>
                <Input 
                  id={setting.key}
                  value={formValues[setting.key] || ''}
                  onChange={(e) => handleInputChange(setting.key, e.target.value)}
                  placeholder={setting.description}
                  type="email"
                />
                {setting.description && (
                  <p className="text-sm text-muted-foreground">{setting.description}</p>
                )}
              </div>
            ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsAdmin;
